import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button, Input, message, Switch } from 'udesk-ui';
import Page from 'Component/common/udesk-qa-page-layout';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import { getOuterApiConfigs } from 'src/api/outerApiConfigs';
import { putOuterApiConfigsById, deleteOuterApiConfigsById } from 'src/api/outerApiConfigs/{id}';
import { InspectionPointSystemDialogFoundResponse } from 'src/api/types';
import './style.scss';

const Template = React.memo((props: any) => {
    const { current: locales } = useRef(UdeskLocales['current']);
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<InspectionPointSystemDialogFoundResponse[]>([]);
    const [paging, setPaging] = useState({
        pageNum: 1,
        pageSize: 20,
        total: 0,
    });
    const ref = useRef<any>({});

    const queryList = useCallback(
        ({
            pageSize,
            pageNum,
            keyword,
        }: {
            pageSize: number;
            pageNum: number;
            keyword?: string;
        }) => {
            setLoading(true);
            getOuterApiConfigs({
                params: {
                    pageNum,
                    pageSize,
                    keyword,
                },
            }).then((resp) => {
                setDataSource(resp.data || []);
                setLoading(false);
                setPaging(resp.paging as any);
            });
        },
        []
    );

    useEffect(() => {
        queryList({ pageSize: 20, pageNum: 1 });
    }, [queryList]);

    const onSearch = (keyword: string) => {
        queryList({ ...paging, pageNum: 1, keyword });
    };

    const paginationProps = useMemo(() => {
        const { pageSize, total } = paging;
        return {
            current: paging.pageNum,
            pageSize,
            total,
            onChange: (page, pageSize) => {
                const newPages = {
                    ...paging,
                    pageNum: page,
                    pageSize,
                };
                queryList({ ...newPages });
                setPaging(newPages);
            },
        };
    }, [paging, queryList]);

    const toggleStatus = useCallback(
        (checked, id) => {
            putOuterApiConfigsById({ status: checked ? 1 : 0 }, { segments: { id } }).then(
                (res) => {
                    message.success(
                        /* 操作成功 */ UdeskLocales['current'].pages.semanticIntelligence
                            .externalInterface.index.operationSuccessful
                    );
                    queryList({
                        pageNum: paging.pageNum,
                        pageSize: paging.pageSize,
                    });
                }
            );
        },
        [paging.pageNum, paging.pageSize, queryList]
    );

    const columns = useMemo(
        () => [
            {
                title: /* 名称 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.name,
                dataIndex: 'apiName',
                key: 'apiName',
            },
            {
                title: /* 接口描述 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.interfaceDescription,
                dataIndex: 'apiDesc',
                key: 'apiDesc',
            },
            {
                title: /* 数据源 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.dataSource,
                dataIndex: 'inspectDataSourceList',
                key: 'inspectDataSourceList',
                render: (v, item) => {
                    return v.map((item) => Udesk.enums.inspectDataSources.getName(item)).join(',');
                },
            },
            {
                title: /* 请求方法 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.requestMethod,
                dataIndex: 'requestMethod',
                key: 'requestMethod',
                render: (v, item) => {
                    return v === 1 ? 'GET' : 'POST';
                },
            },
            {
                title: /* 请求地址 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.requestAddress,
                dataIndex: 'requestUrl',
                key: 'requestUrl',
            },
            {
                title: /* 创建时间 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.creationTime,
                dataIndex: 'createTime',
                key: 'createTime',
            },
            {
                title: /* 有效性 */ UdeskLocales['current'].pages.semanticIntelligence
                    .externalInterface.index.validity,
                dataIndex: 'status',
                key: 'status',
                render: (v, item) => {
                    return (
                        <Switch
                            checked={v === 1}
                            onChange={(checked) => toggleStatus(checked, item.id)}
                        />
                    );
                },
            },
            {
                dataIndex: 'actions',
                title: locales.labels.action,
                renderType: 'actions',
                onEdit: (item) => {
                    let routeOptions = {
                        history: props.history,
                        routeName: 'externalInterfaceEdit',
                        pathParams: { id: item.id },
                        state: {},
                    };
                    Udesk.ui.routing.transitionTo(routeOptions);
                },
                onDelete: (item) => {
                    deleteOuterApiConfigsById({
                        segments: {
                            id: item.id,
                        },
                    }).then((res) => {
                        message.success(locales.business.notifyMessage.deleteSuccess);
                        queryList({
                            pageNum: 1,
                            pageSize: paging.pageSize,
                        });
                    });
                },
            },
        ],
        [
            locales.business.notifyMessage.deleteSuccess,
            locales.labels.action,
            paging.pageSize,
            props.history,
            queryList,
            toggleStatus,
        ]
    );

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
        };
    }, [columns, dataSource]);

    const createBtn = (
        <Button
            onClick={() => {
                let routeOptions = {
                    history: props.history,
                    routeName: 'externalInterfaceNew',
                    state: {},
                };
                Udesk.ui.routing.transitionTo(routeOptions);
            }}
        >
            {locales.labels.create}
        </Button>
    );

    return (
        <Page
            pageBodyClassName="knowledge-base-page-index"
            title={
                /* 外部接口 */ UdeskLocales['current'].pages.semanticIntelligence.externalInterface
                    .index.externalInterface
            }
            padding={true}
        >
            <CommonTablePage
                ref={ref}
                loading={loading}
                mainButton={createBtn}
                tableHeaderLeftRender={
                    <Input.Search
                        placeholder={
                            /* 请输入关键词 */ UdeskLocales['current'].pages.semanticIntelligence
                                .externalInterface.index.pleaseEnterAKeyword
                        }
                        style={{ width: 250 }}
                        onSearch={onSearch}
                    />
                }
            >
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
